/**
 * helpDrawerDefaults.js — iter-2.40
 *
 * Shared data for ContextHelpDrawer and HelpDrawerPanel (admin editor).
 * Contains PANEL_TIPS (per-panel sections+tips) and ALWAYS_NEXT_TIP (fallback
 * next-step guidance) exported as named constants so both files stay in sync.
 *
 * When the admin saves a panel override via HelpDrawerPanel, the saved data
 * takes precedence over these defaults in the drawer.
 */

/* ─── Rich tip content ────────────────────────────────────────────────── */
// Links: [visible text](panel_key) → rendered as amber chips.

export const PANEL_TIPS = {

  /* ── HOME ── */
  home: {
    nextStep: { panel: 'variables', reason: 'Set genre, tone, and target word count before writing so every AI suggestion fits your story.' },
    sections: [
      {
        title: 'Starting your first project',
        tips: [
          'Click "New Project" in the File menu (top-left) to create a blank canvas. Give it a working title — you can rename it any time.',
          'The [Project Variables](variables) panel is the first place to fill in: genre, sub-genre, narrative POV, and tone. These values are injected into every AI prompt.',
          'Open [Chapters](chapters) to add your first chapter and click the Draft button to begin AI-assisted writing.',
          'Auto-save fires every 90 seconds. Use Ctrl+S (⌘S on Mac) for an instant save at any time.',
        ],
      },
      {
        title: 'The workspace layout',
        tips: [
          'The Activity Bar (left strip) holds your panel icons. Click any icon to open that panel.',
          'The Menu Bar (top) provides File, Edit, Tools, View, and Help menus — same structure as Microsoft Word.',
          'The central Editor is where you write. The right-side panels (Chat, Analysis) provide AI assistance without leaving the editor.',
          'If a panel is in the way, click its icon in the Activity Bar to hide it.',
        ],
      },
      {
        title: 'Recommended first-draft order',
        tips: [
          '1. Fill [Project Variables](variables) — genre, POV, tone, target word count.',
          '2. Add characters in the [Character Bible](bible) — minimum Name, Role, and one Trait per major character.',
          '3. Open [Chapters](chapters) → add chapters → click Draft on chapter 1.',
          '4. After 3–5 chapters, run [Analysis](analysis) to catch structural issues early.',
          '5. Use [AI Chat](chat) at any point to brainstorm, plot-check, or ask for scene help.',
        ],
      },
      {
        title: 'Getting help when you\'re stuck',
        tips: [
          'Press F1 to open the full manual. This Help drawer covers panel-specific guidance; F1 covers every feature in depth.',
          '"The AI isn\'t responding" — check your token wallet under File → Buy Tokens. If it\'s zero, top up first.',
          '"The publish button is greyed out" — your editing request is not complete yet. Wait for the editor\'s email.',
          '"It logged me out" — sessions slide forward while you work; idle for 60+ min means a fresh sign-in is needed.',
          'Post in Community (top toolbar → Community → App Help & Bug Reports) — admins read it daily.',
        ],
      },
    ],
  },

  /* ── EDITOR ── */
  editor: {
    nextStep: { panel: 'chapters', reason: 'Use the Chapters panel to manage status, navigate between chapters, and run transitions analysis.' },
    sections: [
      {
        title: 'Writing and generating prose',
        tips: [
          'The central textarea is your live chapter editor. Prose typed or generated here saves automatically.',
          'The "Draft Chapter" button in [Chapters](chapters) sends the full novel context to the AI and streams a complete chapter into the editor.',
          'To continue from your cursor position, press Ctrl+J (⌘J). The AI reads everything above the cursor and continues.',
          'Highlight any passage and right-click to Rewrite, Shorten, Expand, or Change Tone — each uses a micro-edit that preserves surrounding prose.',
        ],
      },
      {
        title: 'Toolbar functions',
        tips: [
          'Word count badge (bottom-right): shows live word count for the open chapter. For chapter targets and Claude Expand, see [Word Count](word_count).',
          'Commit button: saves the current content and marks it as a confirmed revision checkpoint.',
          'Find/Replace (Ctrl+F): searches the open chapter. "Replace All" replaces every occurrence in one pass.',
          'Undo / Redo (Ctrl+Z / Ctrl+Y): step through your edit history within the current session.',
        ],
      },
      {
        title: 'Rename character or place across manuscript',
        tips: [
          'Purpose: Change a character or place name everywhere in one action — renames every chapter and the matching [Character Bible](bible) entry simultaneously.',
          'Why use it: Manual find-replace misses capitalisation variants and can leave the Characters list out of sync.',
          'In the top menu bar click Tools → "Rename character / place across manuscript".',
          'Type the old name exactly as it appears (case-insensitive), then the new name, then click Rename everywhere.',
          'A toast confirms how many occurrences were replaced across how many chapters.',
          'Next step: Re-run [Analysis](analysis) to confirm the renamed character still reads consistently.',
        ],
      },
      {
        title: 'AI assist shortcuts',
        tips: [
          'Ctrl+Shift+R — open Revision Mode in the [Modes](modes) panel. The AI rewrites paragraphs to improve clarity.',
          'Ctrl+Shift+A — run an arc analysis on the current chapter.',
          'The [Live Coach](coach) (pin icon, bottom-right) watches your prose in real time and nudges you when tension drops.',
          'Coach Actions in the [Chapters](chapters) toolbar lets you Add a Scene Beat, Find an Insertion Point, or Rewrite a Scene.',
        ],
      },
    ],
  },

  /* ── CHAPTERS ── */
  chapters: {
    nextStep: { panel: 'bible', reason: 'Adding characters to the Bible before writing keeps the AI consistent on personality, voice, and relationships.' },
    sections: [
      {
        title: 'Chapter status workflow',
        tips: [
          'Each chapter has a status badge: Planned → Outline → Draft → Revised → Final. Click the badge to advance it.',
          '"Draft" means AI prose has been generated. "Final" means you have accepted the content.',
          'The progress bar at the top of the panel shows what % of chapters are drafted — your primary goal for a first pass.',
          'Drag a chapter row up or down to reorder. The book exports in this order.',
        ],
      },
      {
        title: 'Toolbar buttons explained',
        tips: [
          'New (+) — adds a blank chapter at the end of the list. Chapters are numbered automatically.',
          'Analyze — opens the [Analysis](analysis) transitions drawer, which checks chapter-to-chapter flow.',
          'Voice — launches the Voice Coach drawer. It audits prose voice and character voice for drift.',
          'Arc — opens the Arc Walker, which scores each chapter\'s emotional tension and character arc.',
          'Plot (pill) — runs a structural check for all six commercial-fiction plot elements.',
        ],
      },
      {
        title: 'AI Chapter Title Suggester',
        tips: [
          'Purpose: The AI reads your chapter prose and offers 3 distinct title ideas — action-driven, thematic, and atmospheric — each with a one-sentence explanation.',
          'Why use it: Generic "Chapter N" titles miss the chance to build intrigue and signal theme. Three options in 10 seconds is faster than staring at a blank field.',
          'Prerequisite: The chapter must have at least a paragraph of prose content written.',
          'Hover over any chapter row and click the wand icon that appears to the right of the title.',
          'A banner drops in showing 3 clickable title options. Click any to apply it immediately.',
          'Don\'t like any? Click "None of these? Type your own" to rename manually.',
          'Next step: Once titled, advance the chapter status from Draft to Revised.',
        ],
      },
      {
        title: 'Plot Readiness Check',
        tips: [
          'Purpose: Verifies all six required structural elements are present — Exposition, Inciting Incident, Rising Action, Climax, Falling Action, and Resolution.',
          'Why use it: Missing even one element (especially Inciting Incident or Climax) is a hard rejection from publishers. Catching it in draft saves months of revision.',
          'Prerequisite: At least 5 chapters with meaningful prose. Stubs and notes-only chapters give unreliable results.',
          'Open [Chapters](chapters) → click the Plot pill in the toolbar.',
          'Scores: 83–100% = all elements present. 50–82% = some weak. Below 50% = major structural work needed.',
          'Click any missing element to trigger "Rebuild Section" — the AI drafts a 300–500 word scene for that beat.',
          'Next step: After passing Plot Readiness, run [Full Novel Analysis](analysis) to check character, theme, and dialogue quality.',
        ],
      },
      {
        title: 'Subplot Arc Tracker',
        tips: [
          'Purpose: Shows a chapter-by-chapter matrix of which subplots are active, dormant, or resolved — preventing orphaned threads that open but never close.',
          'Why use it: Unresolved threads (a love triangle, a mentor\'s secret, a childhood trauma) are among the most frequent "disappointed" reader reviews.',
          'Prerequisite: At least 3 complete chapters. The tracker analyses your actual prose.',
          'Open [Chapters](chapters) → click the Subplots tab in the chapter analysis section.',
          'Heatmap colours: green (active), grey (dormant), orange (over-active), red (never resolved).',
          'Click any subplot row for a one-paragraph summary of that thread\'s current state.',
          'For unresolved subplots, click "Suggest resolution" — the AI drafts a brief scene that closes the thread.',
          'Next step: Run [Full Novel Analysis](analysis) to confirm theme and tension scores held up.',
        ],
      },
    ],
  },

  /* ── BIBLE ── */
  bible: {
    nextStep: { panel: 'editor', reason: 'With characters defined, open a chapter in the editor and start drafting — the AI will weave your characters in naturally.' },
    sections: [
      {
        title: 'Character depth — the core fields',
        tips: [
          '"Name" — used verbatim in every AI prompt. Match the spelling you want in the prose exactly.',
          '"Role" — Protagonist / Antagonist / Supporting / Minor. Role affects how much prominence the AI gives this character.',
          '"Archetype" — e.g. "The Mentor", "The Trickster". Sets the emotional register the AI uses for dialogue and decisions.',
          '"Traits" — 3–5 positive traits like "Loyal, Perceptive, Self-deprecating".',
          '"Flaws" — 1–3 genuine flaws like "Impulsive, Dishonest with herself". Flaws create natural story tension.',
          '"Arc Summary" — how this character changes from start to end. Grounds every AI continuation.',
        ],
      },
      {
        title: 'Advanced character fields',
        tips: [
          '"Want vs Need" — the Want is the surface goal ("become CEO"), the Need is the inner truth ("acceptance"). When they conflict, the best scenes happen.',
          '"Voice" — paste 2–3 lines of this character\'s dialogue style. The AI uses it to keep dialogue authentic across chapters.',
          '"Bible" — freeform backstory, relationships, secrets. Injected into AI context whenever this character is relevant.',
          '"Relationships" — how this character relates to others. e.g. "Fears Marcus but owes him a debt". Informs AI conflict scenes.',
        ],
      },
      {
        title: 'Bible best practices',
        tips: [
          'Add supporting characters as soon as they appear in dialogue — even just Name + Role keeps the AI consistent.',
          'Use "Find Duplicates" (header button) to detect characters added twice under different spellings.',
          'The bible is injected into EVERY AI prompt for this project. Keep entries concise — verbose bibles slow generation.',
          'Next step: If you need to rename a character after drafting, use [Rename across manuscript](editor) in the Tools menu.',
        ],
      },
    ],
  },

  /* ── ARC ── */
  arc: {
    nextStep: { panel: 'analysis', reason: 'After reviewing arc scores, run a full Analysis pass to get specific plot-hole and transition reports.' },
    sections: [
      {
        title: 'Reading the arc chart',
        tips: [
          'The horizontal axis = chapter numbers. The vertical axis = arc intensity (0–100). Higher bars mean more emotional/narrative intensity.',
          'The amber dashed "Convergence" line marks the chapter where character arcs are most aligned — usually the story\'s climax.',
          'A healthy arc climbs steadily left to right, with a sharp peak around 80–90% of the story, then a brief drop at resolution.',
          'A flat plateau through the middle chapters usually means the protagonist needs a harder obstacle to raise stakes.',
        ],
      },
      {
        title: 'The arc dimensions',
        tips: [
          '"Internal" — tracks psychological tension: self-doubt, internal conflict, emotional revelation.',
          '"External" — tracks plot-level conflict: physical danger, obstacles, confrontations.',
          '"Relational" — tracks relationship tension: rivalry, betrayal, intimacy, forgiveness.',
          '"Moral" — tracks ethical stakes: right vs. wrong choices, the character\'s values under pressure.',
          'Each dimension peaks at a different chapter — hover the Narrative Peaks panel to see plain-English descriptions.',
        ],
      },
      {
        title: 'Using arc scores to improve your story',
        tips: [
          'Low tension in chapters 4–7? Add a subplot complication or raise stakes for a secondary character.',
          'A spike too early (before chapter 6 in a 10-chapter novel)? Delay the reveal by one scene.',
          'Click "Generate Arc Scores" to refresh after writing new chapters — scores update in ~5 seconds.',
          'Arc scores do NOT run an LLM — they use mention-frequency analysis, so generation is instant and free.',
          'Next step: After scoring, run [Full Novel Analysis](analysis) for narrative-level cross-chapter problems.',
        ],
      },
    ],
  },

  /* ── ANALYSIS ── */
  analysis: {
    nextStep: { panel: 'chat', reason: 'After reviewing analysis findings, open AI Chat to brainstorm solutions for the top issues.' },
    sections: [
      {
        title: 'Chapter Analysis — 8 dimensions',
        tips: [
          'Purpose: Sends your open chapter to the AI for scored feedback across 8 craft dimensions with specific, actionable notes naming characters, scenes, and passages.',
          'The 8 dimensions: Continuity, Pacing, Characters, Plot, Theme, Dialogue, Tension (all 0–100), plus Readability (grade level).',
          'Prerequisite: At least one chapter with a complete scene (~500 words). Stubs give unreliable scores.',
          'Open the Analysis panel → click "Analyse This Chapter". Wait 10–20 seconds.',
          'A Tension score below 50 usually means no antagonist force is present. A Characters score below 60 means flat dialogue or no visible character decision.',
          'Re-run analysis after making fixes — scores update immediately and resolved issues disappear from the list.',
          'Next step: Click "Fix Issues" to open the Issue Repair Modal and work through critical issues.',
        ],
      },
      {
        title: 'Full Novel Analysis',
        tips: [
          'Purpose: Reads every drafted chapter at once and returns manuscript-level scores on all 8 dimensions, a per-chapter sentiment arc, and cross-chapter character tracking.',
          'Why use it: Individual chapter analysis misses novel-level patterns — a character who vanishes for 6 chapters, or a theme introduced but never developed.',
          'Prerequisite: A complete or near-complete first draft (ideally all chapters in Draft or Revised status).',
          'Open the Analysis panel → click "Analyse Full Novel". Wait 30–60 seconds.',
          'The Sentiment Arc chart shows emotional tone per chapter on a -1 (dark/tragic) to +1 (hopeful/triumphant) scale.',
          'A flat sentiment arc across the middle chapters is the most common cause of "sagging middle" feedback.',
          'Next step: Use the Issue Repair Modal to fix critical issues before sending to the editing service.',
        ],
      },
      {
        title: 'Issue Repair Modal',
        tips: [
          'Purpose: A focused fix-loop that shows each analysis issue one at a time — manually fix or auto-fix with Claude — then re-runs analysis immediately so you see the score change.',
          'Why use it: Analysis tells you what\'s wrong. Issue Repair tells you how to fix it and applies the fix without interrupting your flow.',
          'Prerequisite: A chapter with at least one issue from a completed Chapter Analysis.',
          'In the Analysis panel → click "Fix Issues" on the chapter results row.',
          'For each issue: read the severity badge (red = critical, amber = warning, green = info), then (A) type your own fix, or (B) click "Auto-fix with Claude".',
          'Click "Apply & Save" — the modal re-runs analysis on the updated text and shows Before vs. After scores.',
          'Fix red (critical) issues before amber (warning) ones — resolving a plot hole often clears a character-drift warning caused by it.',
          'If Claude\'s auto-fix misses the mark, accept it as a starting point and edit in the text area before applying.',
        ],
      },
      {
        title: 'Continuity Tracking',
        tips: [
          'Purpose: AI scan across your manuscript for consistency errors — character descriptions that change, objects that teleport, timeline gaps.',
          'Why use it: Continuity errors are invisible to their author and glaring to every reader. Catching them in draft costs minutes; after publication, they cost reviews.',
          'Prerequisite: At least 3–4 complete chapters with cross-chapter data to identify discrepancies.',
          'In the Analysis panel → click the Continuity tab → click "Run Continuity Scan". Results appear within 20–40 seconds.',
          'Results are grouped by type: Character, Object, Timeline. Each item shows which chapters conflict with a quoted passage from each.',
          'Click "Jump to Chapter" on any conflict to open that chapter in the editor with the conflicting passage highlighted.',
          'Use the [Character Bible](bible) as your canonical reference — if it\'s in the Bible, it\'s correct.',
          'Next step: After resolving continuity errors, run Issue Repair to address any remaining narrative issues.',
        ],
      },
    ],
  },

  /* ── CHAT ── */
  chat: {
    nextStep: { panel: 'marketing', reason: 'Once you are happy with the story, move to the Marketing panel to build your blurb and identify your audience.' },
    sections: [
      {
        title: 'What to ask the AI',
        tips: [
          '"What plot holes exist in my story so far?" — the AI reads all your chapters and gives a fresh perspective.',
          '"Does my protagonist\'s arc feel complete?" — checks whether the character\'s Want and Need (from the [Character Bible](bible)) have been addressed.',
          '"Suggest 3 ways to raise tension in Chapter 5 without adding new characters" — targeted, constrained help.',
          '"Write a 100-word blurb for this novel" — a quick first draft of your back-cover copy.',
          '"Is this pacing too slow for the thriller genre?" — genre-aware pacing feedback.',
        ],
      },
      {
        title: 'How context works',
        tips: [
          'The AI has the full text of every drafted chapter, your [Character Bible](bible), and your [Project Variables](variables). You never need to re-explain the story.',
          'Ask follow-up questions in the same session — the conversation accumulates context.',
          'Each chat session is independent. Start a new session to reset context for a fresh perspective.',
          'For very long novels (20+ chapters), the AI summarises older chapters to fit context. Recent chapters always get full detail.',
        ],
      },
      {
        title: 'Community & peer support',
        tips: [
          'Click Community in the top toolbar to open the writers\' forum.',
          'Five topics: Introductions, Craft & Technique, Publishing & Marketing, Feedback Requests, App Help & Bug Reports.',
          'Admins read the App Help & Bug Reports topic daily.',
          'When someone replies to your thread, you\'ll get an email even if you\'ve closed the app.',
        ],
      },
    ],
  },

  /* ── MARKETING ── */
  marketing: {
    nextStep: { panel: 'bookshelf', reason: 'When marketing materials are ready, head to My Bookshelf to move the project to "Finished" and publish to the Bookstore.' },
    sections: [
      {
        title: 'Demand Opportunities panel',
        tips: [
          '"Demand Opportunities" shows trending sub-genres and reader demand signals from the marketplace.',
          '"Comp titles" field — enter 2–3 comparable books. The AI uses them to calibrate tone suggestions for your blurb.',
          'Write your blurb before you finish the first draft — summarising forces you to identify the core conflict, which tightens the actual story.',
        ],
      },
      {
        title: 'Royalties and earnings',
        tips: [
          'The Royalties dashboard (File → My Royalties) shows: Lifetime earnings, This Month, Pending Payout, and Paid To Date.',
          'Click the "By Book" tab to see which titles are selling, and "Monthly Trend" to spot which marketing pushes worked.',
          'Default royalty split: 70% to you, 30% to Crown & Rose.',
          '"Break-even" for a self-published ebook at $4.99: typically 80–120 sales to recover typical editing + cover costs.',
        ],
      },
      {
        title: 'Tokens and payments',
        tips: [
          'Every AI request burns a small number of tokens. New accounts get a starter pack to try things for free.',
          'Buy tokens: click your token counter (top-right) → pick a pack ($5, $20, $50). Bigger packs = lower price per token.',
          'Pay with Stripe (secure card-payment pop-up). Tokens land in your wallet within seconds.',
          'Buy tokens monthly rather than tiny top-ups — fewer Stripe fees mean more writing power per dollar.',
        ],
      },
    ],
  },

  /* ── MODES ── */
  modes: {
    nextStep: { panel: 'editor', reason: 'Once you have chosen the right mode, return to the Manuscript Editor to write using that mode\'s AI settings.' },
    sections: [
      {
        title: 'Choosing the right mode',
        tips: [
          '"Draft Mode" (default) — the AI writes longer continuations with full narrative detail. Best for first-draft velocity.',
          '"Revision Mode" — the AI shortens, sharpens, and suggests improvements to existing prose. Best for polishing after a complete draft.',
          '"World Builder" — expands settings, lore, magic systems, and environment descriptions. Best for high-concept or speculative fiction.',
          '"Dialogue Focus" — prioritises naturalistic dialogue and character voice, minimising description. Best for character-driven literary fiction.',
        ],
      },
      {
        title: 'When to switch modes',
        tips: [
          'Switch to Revision Mode once you have a complete first draft (all chapters in "Draft" status).',
          'Use World Builder selectively — generating too much world detail early can over-constrain your plot.',
          'Mode only affects AI continuations, not your own typed prose. You can type in any mode.',
          'Settings (File → Settings): change editor font, theme, AI model (Sonnet vs. Opus), and interface language.',
        ],
      },
    ],
  },

  /* ── VARIABLES ── */
  variables: {
    nextStep: { panel: 'bible', reason: 'After setting project variables, populate the Character Bible so the AI has characters to write about.' },
    sections: [
      {
        title: 'Core generation settings',
        tips: [
          '"Genre" and "Sub-genre" — the most important fields. Genre shapes every AI prompt. Updating genre mid-project is safe.',
          '"Narrative POV" — First Person, Third Person Limited, or Omniscient. Set it once and don\'t change mid-draft.',
          '"Tone" — one or two words like "Dark, Atmospheric" or "Witty, Commercial". Combined with Genre, this is the single biggest driver of AI voice.',
          '"Target word count" — the total novel length you\'re aiming for (e.g. 80,000). Used to calibrate the arc and [Word Count](word_count) panel.',
        ],
      },
      {
        title: 'Advanced settings',
        tips: [
          '"Words per chapter" — the target length per AI draft. 2,000–3,000 words is commercial fiction standard.',
          '"AI Model" — choose the model that drafts this project. Studio-tier users can pick Opus for higher literary quality.',
          '"Chapter count" — sets how many chapters the arc visualiser plans around. Changeable at any time.',
          '"Series settings" — toggle "Is Series" to unlock the book number and Voice Lock feature for series voice consistency.',
          'A Variable is a reusable text snippet. Define it once (e.g. city_name = Mistgarde) and use {{ city_name }} anywhere in the editor.',
        ],
      },
    ],
  },

  /* ── BOOKSHELF ── */
  bookshelf: {
    nextStep: { panel: 'home', reason: 'Published? Start your next project from the Home panel.' },
    sections: [
      {
        title: 'Pipeline stages',
        tips: [
          '"Drafting" — the project is actively being written. All panels are fully editable.',
          '"Revised" — move here once every chapter is marked Draft or Final and you\'ve completed your editorial pass.',
          '"Finished" — move here when the book is ready for readers. The Publish button becomes active.',
          '"Published" — the book is live on the Crown & Rose Bookstore. Royalties are tracked in [Marketing](marketing).',
        ],
      },
      {
        title: 'Publishing checklist',
        tips: [
          'Before clicking Publish: (1) all chapters Final, (2) Arc score ≥ 75 in [Arc Visualizer](arc), (3) Voice Coach audit passed, (4) cover image uploaded.',
          'Cover-art tiers: Basic (free), Pro ($49), or Custom ($199 + Stripe for a human designer).',
          'After publishing, use the [Marketing](marketing) dashboard to track sales and reader engagement.',
        ],
      },
      {
        title: 'Professional editing service',
        tips: [
          'Every manuscript must pass the editing & proofreading service before listing in the Crown & Rose Bookstore.',
          'When finished: File → Publish to Bookstore… The app counts words and shows the matching tier price.',
          'Confirm payment. Tokens leave your wallet and your manuscript enters the editor queue.',
          'When the editor marks the book completed, you\'ll get an email. Open the same dialog to publish.',
          'Next step: After the editor returns the manuscript, run a final [Analysis](analysis) pass before clicking Publish.',
        ],
      },
      {
        title: 'Export formats',
        tips: [
          'Choose File → Export… to download your book in PDF, DOCX, EPUB, Markdown (.md), or Plain text (.txt).',
          'PDF is printable and device-consistent. DOCX opens in Word or Google Docs. EPUB is for Kindle and e-readers.',
          'Export a backup once a week. Your work lives in the cloud, but a local copy is good insurance.',
        ],
      },
    ],
  },

  /* ── WORD COUNT ── */
  word_count: {
    nextStep: { panel: 'analysis', reason: 'After expanding a chapter with Claude, run Chapter Analysis to verify pacing and tension held up through the added prose.' },
    sections: [
      {
        title: 'What the Word Count Panel shows',
        tips: [
          'Purpose: Shows your current chapter word count, your target, and the gap — colour-coded so you see at a glance whether the chapter is short, on target, or long.',
          'The manuscript total bar shows your overall progress against the novel\'s target from [Project Variables](variables).',
          'Amber gap pill = you\'re short. Green = you\'ve hit the target.',
          'The panel lives below the chapter toolbox inside the editor. Click anywhere on the bar (or the chevron) to expand it.',
          'Pin the panel open (pin icon, top-right of the bar) to keep word counts visible while you write.',
        ],
      },
      {
        title: 'Setting chapter and manuscript targets',
        tips: [
          'In the Chapter target input, type your target words for this chapter (e.g. 2,500). Press Enter or click away — saves automatically.',
          'Commercial fiction norms: 70,000–100,000 words for a novel, 2,000–4,000 words per chapter.',
          'Your overall novel target is set in [Project Variables](variables) → Target word count field.',
          'Chapters that run short signal under-developed scenes, thin dialogue, or skipped interiority — fix it before revision, not after.',
        ],
      },
      {
        title: 'Claude Expand — AI chapter expansion',
        tips: [
          'Purpose: Claude deepens scenes, extends dialogue, and fleshes out action beats to reach your target — without changing plot events or adding new characters.',
          'Why use it: Short chapters feel rushed and erode pacing. Expanding before revision gives your editor something to sharpen rather than something to pad.',
          'Prerequisite: Indie Author subscription tier or above. Scribe tier does not include Claude Expand. See [Subscriptions](tiers).',
          'If the chapter is short, click "Expand with Claude". Expanded text appears in a preview pane below the panel.',
          'Read the preview, then click Apply to replace the chapter content, or Discard to keep the original.',
          'The expansion preserves your plot — it adds depth and texture, not new story events.',
          'Next step: Run [Chapter Analysis](analysis) after expanding to verify pacing, tension, and character scores held up.',
        ],
      },
    ],
  },

  /* ── NARRATION ── */
  narration: {
    nextStep: { panel: 'offline', reason: 'After generating audio chapters, set up Offline Narration so you and beta readers can listen without an internet connection.' },
    sections: [
      {
        title: 'Why narrate your book',
        tips: [
          'Audiobooks reach 45% of readers who never buy ebooks — adding discoverability on audio platforms.',
          'Listening to your own prose surfaces awkward sentences and pacing problems that your eye misses on re-reads.',
          'Self-published audio can double revenue per title compared to ebook-only publishing.',
          'Prerequisite: Publisher tier subscription or above. An active ElevenLabs or Fish Audio API key (add it under File → Settings → Narration). See [Subscriptions](tiers).',
        ],
      },
      {
        title: 'Setting up the Narration Studio',
        tips: [
          'Open a project. In the File menu, click "Narration Studio".',
          'In the Narrator tab, choose a voice actor from the gallery. Click Preview to hear a 10-second sample.',
          'Voice actors differ in pace, timbre, and accent — pick one that fits your genre. A darker thriller benefits from a slower, deeper voice.',
          'ElevenLabs voices offer more emotional range. Fish Audio voices are faster and cheaper per minute.',
        ],
      },
      {
        title: 'Generating and downloading audio',
        tips: [
          'Switch to the Generate tab. Click Generate next to a chapter to narrate it. Audio streams in within 30–60 seconds.',
          'Press Play to listen inline. Click Download to save the .mp3 file.',
          'For bulk narration, click "Generate All" — chapters queue and process one at a time.',
          'A narrated 15-minute chapter is approximately 10–15 MB in .mp3 format.',
          'Next step: Go to the Downloads tab and use the Offline Setup guide to cache chapters on your device (see [Offline Audio](offline)).',
        ],
      },
    ],
  },

  /* ── OFFLINE ── */
  offline: {
    nextStep: { panel: 'bookshelf', reason: 'Once audio is cached and ready, move the project to Finished in My Bookshelf and prepare to publish.' },
    sections: [
      {
        title: 'Why cache audio offline',
        tips: [
          'Purpose: Caches narrated audio chapters on your device so they play without an internet connection, using your browser\'s Service Worker.',
          'Writers and beta readers want to listen while commuting, travelling, or in areas with poor signal.',
          'Caching a chapter takes 30 seconds; the benefit lasts until you clear the cache.',
          'Prerequisite: At least one chapter narrated in the [Narration Studio](narration). Access to the Downloads tab.',
        ],
      },
      {
        title: 'Platform setup guide',
        tips: [
          'In the Narration Studio → Downloads tab → click "Setup Guide →" in the offline note banner. The dialog detects your platform automatically.',
          'iOS (Safari): Tap Share → Add to Home Screen. Reopen from your Home Screen. Audio caches automatically on first play.',
          'Android (Chrome): Tap the three-dot menu → Add to Home Screen. Chapters cache on first play — no further steps needed.',
          'Desktop (Chrome/Edge): Chapters cache on first play in any modern browser. Optionally install as a PWA via the address bar icon.',
          'iOS Safari requires the "Add to Home Screen" install step for persistent caching. Without it, audio only caches for the current session.',
        ],
      },
      {
        title: 'Managing your offline cache',
        tips: [
          'Toggle "Offline Mode" in the dialog to see cache status: chapters cached and MB used.',
          'Click "Clear All" to free device storage.',
          'A cached 15-minute chapter uses approximately 10–15 MB. Cache 5 chapters for a 75-minute offline listening session.',
          'Next step: Share the app URL with a beta reader and walk them through the iOS or Android setup so they can listen during their commute.',
        ],
      },
    ],
  },

  /* ── TIERS ── */
  tiers: {
    nextStep: { panel: 'word_count', reason: 'Once upgraded to Indie Author or above, try the Claude Expand button in the Word Count Panel.' },
    sections: [
      {
        title: 'What lock icons mean',
        tips: [
          'An open lock icon means the feature is active on your current tier.',
          'A closed lock means the feature requires a higher tier — hover to see exactly which tier unlocks it.',
          'Attempting to use a locked feature shows a clear upgrade prompt, not a silent failure.',
          'AI-gated features (generate, expand, analyse) are verified on the server — tier restrictions cannot be bypassed.',
        ],
      },
      {
        title: 'Tier features breakdown',
        tips: [
          'Scribe — Full drafting, characters, variables, chapter analysis, AI chat, export. Core AI drafting, no audio.',
          'Indie Author — All Scribe features + [Word Count Expand](word_count) (Claude), advanced editing tools. Best for writers who need more control over word targets.',
          'Publisher — All Author features + [Narration Studio](narration) (AI audio), bulk draft, full continuity audit. Best for writers publishing full audiobooks.',
          'Studio — All Publisher features + voice cloning, unlimited word budget, priority support. Best for high-volume or series authors.',
        ],
      },
      {
        title: 'Upgrading your subscription',
        tips: [
          'To upgrade: open File → Subscription. Pick the tier matching the features you need. Changes take effect immediately.',
          'After upgrading, refresh the page if any locked feature still appears closed. New capabilities load at sign-in.',
          'Lock icons show a tooltip with the required tier when you hover — makes comparison-shopping between tiers easy.',
          'Next step: After upgrading to Indie Author, try [Claude Expand](word_count). After upgrading to Publisher, set up your first [Narration Studio](narration) voice.',
        ],
      },
    ],
  },

  /* ── COACH ── */
  coach: {
    nextStep: { panel: 'analysis', reason: 'After drafting with the coach active, run Chapter Analysis to see if the tension score reflects the coach\'s interventions.' },
    sections: [
      {
        title: 'How the Live Coach works',
        tips: [
          'Purpose: An AI that watches your prose as you type. When tension drops below a threshold, it nudges you with a suggestion — a scene beat idea, a conflict insertion point, or a dialogue sharpening note.',
          'Why use it: Writers in "flow state" can\'t assess their own tension levels objectively. The Live Coach acts as a real-time story editor over your shoulder.',
          'Prerequisite: The editor open with at least one full paragraph of prose (~200 words gives the coach enough context for meaningful suggestions).',
          'Click the Coach pin icon in the bottom-right corner of the editor to open the coach popover.',
          'Write normally. When the coach detects a tension drop, a suggestion card appears.',
        ],
      },
      {
        title: 'Acting on coach suggestions',
        tips: [
          'Suggestion examples: "Add an obstacle at line 23" or "Cut to the other character\'s reaction".',
          'Click Apply to insert the suggested beat into your prose, or Dismiss to continue writing your way.',
          'Turn off the coach for exposition scenes — it\'s calibrated for action/conflict beats and will incorrectly flag world-building as "low tension".',
          'Coach Actions in the [Chapters](chapters) toolbar lets you Add a Scene Beat, Find an Insertion Point, or Rewrite a Scene with precise authorial direction.',
        ],
      },
      {
        title: 'Manual tension adjustment',
        tips: [
          'Highlight the text you want to change, right-click, and choose "Adjust Tension".',
          'Use the slider to dial tension up or down. Claude rewrites the passage accordingly.',
          'Dialling down is useful for exposition, backstory, or quiet emotional scenes that feel over-written.',
          'Dialling up is useful for conflict peaks, chase scenes, or arguments that feel flat on re-read.',
          'Next step: After drafting with the coach active, run [Chapter Analysis](analysis) to see if the Tension score reflects the coach\'s interventions.',
        ],
      },
    ],
  },
};

/* ─── Next-step defaults ──────────────────────────────────────────────── */

export const ALWAYS_NEXT_TIP = {
  home:       { panel: 'variables',  reason: 'Configure your project settings.' },
  editor:     { panel: 'chapters',   reason: 'Manage chapter status and navigation.' },
  chapters:   { panel: 'bible',      reason: 'Define characters for richer AI prose.' },
  bible:      { panel: 'editor',     reason: 'Start writing with your characters ready.' },
  arc:        { panel: 'analysis',   reason: 'Dig deeper with full manuscript analysis.' },
  analysis:   { panel: 'chat',       reason: 'Brainstorm fixes with the AI.' },
  chat:       { panel: 'marketing',  reason: 'Turn your finished story into a product.' },
  marketing:  { panel: 'bookshelf',  reason: 'Publish when you are ready.' },
  modes:      { panel: 'editor',     reason: 'Write using your chosen mode.' },
  variables:  { panel: 'bible',      reason: 'Add characters to your bible.' },
  bookshelf:  { panel: 'home',       reason: 'Start your next project.' },
  word_count: { panel: 'analysis',   reason: 'Run Chapter Analysis after expanding.' },
  narration:  { panel: 'offline',    reason: 'Cache audio for offline listening.' },
  offline:    { panel: 'bookshelf',  reason: 'Move the project to Finished.' },
  tiers:      { panel: 'word_count', reason: 'Try Claude Expand after upgrading.' },
  coach:      { panel: 'analysis',   reason: 'Check tension scores after coaching.' },
};
